import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { KeyRound, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MFA_REPLACE_AUTHENTICATOR_PATH, signInPathFor, useAuth, useSignOut } from '@/auth';
import { messages } from '@/i18n';
import { ErrorAlert } from '@/screens/auth/mfa/mfaShared';
import { secondFactorErrorMessage } from '@/screens/auth/mfa/mfaUtils';
import { PhoneEnrollmentStep } from '@/screens/auth/mfa/PhoneEnrollmentStep';
import { TotpEnrollmentStep } from '@/screens/auth/mfa/TotpEnrollmentStep';

function FactorRow({
  icon,
  title,
  detail,
  action,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  action: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <span className="bg-secondary/60 flex size-9 shrink-0 items-center justify-center rounded-full">
          {icon}
        </span>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-muted-foreground text-xs">{detail}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

/**
 * /security — the signed-in user's own second factors. Inside both guards.
 *
 * The list comes from the provider's user record and is display only; the API
 * decides what a factor is good for from the token on every request. Every
 * change here may need a recent sign-in, which the enrollment steps ask for
 * themselves (RecentSignInRequiredError).
 */
export function SecuritySettingsScreen() {
  const t = messages.mfa;
  const { secondFactors, removeSecondFactor } = useAuth();
  const signOut = useSignOut();
  const [editing, setEditing] = useState<'totp' | 'phone' | null>(null);
  const [oldPhoneIds, setOldPhoneIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const totp = secondFactors.filter((f) => f.kind === 'totp');
  const phones = secondFactors.filter((f) => f.kind === 'sms');

  const saved = () => {
    setEditing(null);
    setNotice(t.securitySaved);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 2500);
  };

  // A new recovery phone replaces the old one: enroll first, then remove, then
  // sign in again — removing a factor ends the session.
  const phoneEnrolled = async () => {
    if (oldPhoneIds.length === 0) {
      saved();
      return;
    }
    try {
      for (const id of oldPhoneIds) {
        await removeSecondFactor(id);
      }
    } catch (err) {
      setEditing(null);
      setError(secondFactorErrorMessage(err));
      return;
    }
    await signOut({ redirectTo: signInPathFor('second-factors-changed') });
  };

  return (
    <section className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight">{t.securityTitle}</h1>
      <p className="text-muted-foreground mt-1 mb-6 text-sm">{t.securitySubtitle}</p>

      {error ? (
        <div className="mb-4">
          <ErrorAlert message={error} />
        </div>
      ) : null}

      {/* Always rendered, so the live region exists before it has anything to
          announce. */}
      <p role="status" aria-live="polite" className="text-muted-foreground mb-4 text-sm empty:mb-0">
        {notice ?? ''}
      </p>

      <Card>
        <CardContent className="pt-6">
          {editing === 'totp' ? (
            <TotpEnrollmentStep onEnrolled={saved} onCancel={() => setEditing(null)} />
          ) : editing === 'phone' ? (
            <div className="flex flex-col gap-4">
              {oldPhoneIds.length > 0 ? (
                <p className="text-muted-foreground text-xs">{t.securityChangeNote}</p>
              ) : null}
              <PhoneEnrollmentStep
                onEnrolled={() => void phoneEnrolled()}
                onCancel={() => setEditing(null)}
              />
            </div>
          ) : (
            <div className="divide-border divide-y">
              <FactorRow
                icon={<KeyRound className="size-4" aria-hidden />}
                title={t.securityAuthenticator}
                detail={totp.length ? t.securityInUse : t.securityNotSetUp}
                action={
                  totp.length ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={MFA_REPLACE_AUTHENTICATOR_PATH}>{t.securityReplace}</Link>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setEditing('totp');
                      }}
                    >
                      {t.securitySetUp}
                    </Button>
                  )
                }
              />
              <FactorRow
                icon={<Smartphone className="size-4" aria-hidden />}
                title={t.securityRecoveryPhone}
                detail={phones[0]?.maskedPhone ?? t.securityNotSetUp}
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setError(null);
                      setOldPhoneIds(phones.map((f) => f.id));
                      setEditing('phone');
                    }}
                  >
                    {phones.length ? t.securityChange : t.securityAdd}
                  </Button>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
